import { z } from 'zod';
import { CART_MAX_DISTINCT_ITEMS, CART_MAX_QUANTITY_PER_ITEM } from './cartPolicy.js';

export const availabilityConfidenceSchema = z.enum(['HIGH', 'MEDIUM', 'LOW']);

export const quoteAvailabilitySnapshotItemSchema = z.object({
  productId: z.string().trim().min(1).max(128),
  productName: z.string().trim().min(1).max(300),
  quantity: z.coerce.number().int().min(1).max(CART_MAX_QUANTITY_PER_ITEM),
  confidence: availabilityConfidenceSchema,
  confidenceScore: z.coerce.number().min(0).max(1),
  confidenceSource: z.string().trim().min(1).max(120),
  storeId: z.string().trim().max(128).nullable(),
  storeName: z.string().trim().max(200).nullable()
});

/** Hub→primary→customer vs multi-stop mileage — optional on older quotes. */
export const extraRouteSnapshotSchema = z.object({
  extraStoreCount: z.coerce.number().int().min(0).max(50).optional(),
  detourFactor: z.coerce.number().min(1).max(50),
  baselineRouteMiles: z.coerce.number().min(0).max(5000),
  multiStopRouteMiles: z.coerce.number().min(0).max(5000),
  routeBlockedAtQuote: z.boolean(),
  extraStoreDetourSurchargeUsd: z.coerce.number().min(0).max(500),
  threshold: z.coerce.number().min(1).max(10).optional()
});

export const perLineFulfillmentSchema = z.object({
  productId: z.string().trim().min(1).max(128),
  productName: z.string().trim().min(1).max(300),
  storeId: z.string().trim().min(1).max(128),
  storeName: z.string().trim().min(1).max(200),
  storeAddress: z.string().trim().max(400).optional(),
  unitPriceCents: z.coerce.number().int().min(0).max(1_000_000),
  status: z.string().trim().min(1).max(64),
  fulfillmentSource: z.enum(['hub_inventory', 'retail_passthrough']),
  temperaturePreference: z.enum(['chilled', 'ambient', 'cold', 'either']).optional(),
  temperatureClass: z.enum(['chilled', 'ambient', 'cold']).optional()
});

export const fulfillmentDisclosureSchema = z.object({
  retailers: z.array(z.object({
    storeId: z.string().trim().min(1).max(128),
    storeName: z.string().trim().min(1).max(200),
    storeAddress: z.string().trim().max(400).optional(),
    lineItemIds: z.array(z.string().trim().min(1).max(128)).max(500)
  })).max(50),
  perLineFulfillment: z.array(perLineFulfillmentSchema).max(500),
  multiStoreFeeCents: z.coerce.number().int().min(0).max(1_000_000),
  requiresRetailerAuthorization: z.boolean(),
  requiresLineSubstituteAcknowledgment: z.boolean(),
  substituteOffers: z.array(z.object({
    productId: z.string().trim().min(1).max(128),
    productName: z.string().trim().min(1).max(300),
    reason: z.string().trim().min(1).max(64),
    fromStoreId: z.string().trim().min(1).max(128),
    fromStoreName: z.string().trim().min(1).max(200)
  })).max(500)
});

export const MAX_CUSTOM_REQUESTS_PER_ORDER = 3;

export const customRequestInputSchema = z.object({
  description: z.string().trim().min(1).max(2000),
  storeName: z.string().trim().max(100).optional(),
  imageUrls: z.array(z.string().url()).max(5).optional()
});

/** Server-resolved hold per custom request, frozen at quote time and reused at order create. */
export const customRequestHoldSchema = z.object({
  descriptionKey: z.string().max(200),
  holdCents: z.coerce.number().int().min(0).max(100_000),
  referencePriceCents: z.coerce.number().int().min(0).max(100_000),
  source: z.enum(['catalog', 'receipt_history', 'kroger', 'web_search', 'fallback'])
});

export const orderQuoteSnapshotSchema = z.object({
  generatedAt: z.string().trim().datetime({ offset: true }),
  expiresAt: z.coerce.number().int().positive(),
  distanceMetadata: z.object({
    origin: z.string().trim().min(3).max(300),
    destination: z.string().trim().min(3).max(300),
    distanceMiles: z.coerce.number().min(0).max(5000),
    distanceFee: z.coerce.number().min(0).max(1000),
    distanceFeeCents: z.coerce.number().int().min(0).max(100000),
    signedAt: z.string().trim().datetime({ offset: true }),
    signature: z.string().trim().min(16).max(256)
  }),
  deliveryFees: z.object({
    routeFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    distanceFeeCents: z.coerce.number().int().min(0).max(100000),
    originalDistanceFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    largeOrderFeeCents: z.coerce.number().int().min(0).max(100000),
    heavyItemFeeCents: z.coerce.number().int().min(0).max(100000),
    itemHandlingFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    busyFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    surgeMultiplier: z.coerce.number().min(0.5).max(5).optional(),
    driverSurgeMultiplier: z.coerce.number().min(0.5).max(5).optional(),
    serviceFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    variableFuelSurchargeCents: z.coerce.number().int().min(0).max(100000),
    customRequestsDepositCents: z.coerce.number().int().min(0).max(100000).optional(),
    smallOrderFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    taxCents: z.coerce.number().int().min(0).max(100000).optional(),
    lateNightFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    standaloneReturnFeeCents: z.coerce.number().int().min(0).max(100000).optional(),
    dynamicLogisticsAdjustmentCents: z.coerce.number().int().min(0).max(100000).optional(),
    totalFeeCents: z.coerce.number().int().min(0).max(500000)
  }).passthrough(),
  /**
   * Pre-tip merchandise + fees net (cents). Bound at order-create against recalculated
   * pricingResult.totalCents so fee-only locks cannot miss cart price drift.
   * Optional for legacy in-flight quotes; checkout always emits for new quotes.
   */
  pricing: z
    .object({
      subtotalCents: z.coerce.number().int().min(0).max(5_000_000),
      netCents: z.coerce.number().int().min(0).max(5_000_000)
    })
    .optional(),
  uncertainty: z.object({
    hasUncertainItems: z.boolean(),
    requiresCheckoutAcknowledgment: z.boolean(),
    disclaimer: z.object({
      version: z.string().trim().min(1).max(50),
      message: z.string().trim().min(10).max(1000),
      acknowledgedAtQuote: z.boolean()
    }),
    uncertainItems: z.array(quoteAvailabilitySnapshotItemSchema).max(500)
  }),
  checkoutAcknowledgment: z.object({
    lowConfidenceAccepted: z.boolean(),
    retailerAuthorizationAccepted: z.boolean().optional(),
    lineSubstituteAccepted: z.boolean().optional()
  }),
  fulfillmentDisclosure: fulfillmentDisclosureSchema.optional(),
  pricingPolicySnapshot: z.object({
    version: z.coerce.number().int().min(1),
    deliveryFee: z.coerce.number().min(0).max(1000),
    returnHandlingFeePerContainer: z.coerce.number().min(0).max(100),
    /** Per-container statutory deposit (e.g. Michigan $0.10). */
    depositValuePerContainer: z.coerce.number().min(0).max(100).default(0),
    /** Per-glass-container surcharge applied on top of returnHandlingFeePerContainer. */
    glassHandlingSurchargePerContainer: z.coerce.number().min(0).max(100).default(0),
    generatedAt: z.string().trim().datetime({ offset: true })
  }),
  /**
   * End-to-end quote integrity seal. Generated by checkout and verified by
   * order-create to detect client-side quote tampering between preview/finalize.
   */
  lockSignature: z.string().trim().min(32).max(256),
  tierSnapshot: z.object({
    name: z.string().trim().min(1).max(32),
    version: z.coerce.number().int().min(1),
    capturedAt: z.string().trim().datetime({ offset: true }),
    benefits: z
      .object({
        routeFeeDiscountPercent: z.coerce.number().min(0).max(100).optional(),
        deliveryDiscountPercent: z.coerce.number().min(0).max(100).optional()
      })
      .passthrough()
  }),
  extraRouteSnapshot: extraRouteSnapshotSchema.optional(),
  customRequestHolds: z.array(customRequestHoldSchema).max(MAX_CUSTOM_REQUESTS_PER_ORDER).optional(),
  deliveryCoords: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180)
  }).optional(),
  quoteId: z.string().trim().max(128).optional()
}).passthrough();

const checkoutFinalizationBaseSchema = z.object({
  items: z.array(z.object({
    productId: z.string().trim().min(1).max(128),
    quantity: z.coerce
      .number()
      .int()
      .min(1)
      .max(CART_MAX_QUANTITY_PER_ITEM, `You can order up to ${CART_MAX_QUANTITY_PER_ITEM} of one item per order.`),
    temperaturePreference: z.enum(['chilled', 'ambient', 'cold', 'either']).optional()
  })).min(1).max(CART_MAX_DISTINCT_ITEMS, `An order can include up to ${CART_MAX_DISTINCT_ITEMS} different items.`),
  customRequests: z.array(customRequestInputSchema).max(MAX_CUSTOM_REQUESTS_PER_ORDER).optional(),
  shippingAddress: z.string().trim().min(5).max(300),
  deliveryInstructions: z.string().trim().max(500).optional(),
  selectedStoreId: z.string().trim().regex(/^[a-f\d]{24}$/i, 'selectedStoreId must be a valid ObjectId'),
  requestedDeliveryWindowId: z.string().trim().regex(/^[a-f\d]{24}$/i, 'requestedDeliveryWindowId must be a valid ObjectId').optional(),
  tipCents: z.coerce.number().int().min(0).max(1_000_000).optional(),
  promoCode: z.string().trim().max(32).optional(),
  savedAddressId: z.string().trim().regex(/^[a-f\d]{24}$/i, 'savedAddressId must be a valid ObjectId').optional(),
  savedPaymentMethodId: z.string().trim().regex(/^[a-f\d]{24}$/i, 'savedPaymentMethodId must be a valid ObjectId').optional(),
  extraStoreFeeEstimate: z.coerce.number().min(0).max(1000),
  /** Optional wallet hold at place-order (USD cents). Debited atomically with PENDING_AUTH order write. */
  walletCreditToReserveCents: z.coerce.number().int().min(0).max(1_000_000).optional(),
  /**
   * Return credit is computed server-side from the bottle-return draft (or returnUpcs).
   * CREDIT: estimated return value reduces today’s authorized/card amount.
   * CASH: return payout is separate; today’s charge ignores estimated return credit.
   */
  returnPayoutMethod: z.enum(['CREDIT', 'CASH']).optional(),
  isStandaloneReturn: z.boolean().optional(),
  deliverySpeed: z.enum(['STANDARD', 'PRIORITY_DIRECT']).default('STANDARD').optional(),
  priorityFeeCents: z.coerce.number().int().min(0).max(10_000).default(0).optional(),
  shopperPreferences: z.object({
    allowSubstitutions: z.boolean().default(true),
    refundMissingItems: z.boolean().default(false),
    allowExtraStoreTrip: z.boolean().default(false),
    maxExtraStoreFee: z.coerce.number().min(0).max(50).default(0)
  }).optional(),
  quoteId: z.string().trim().max(128).optional(),
  quoteSnapshot: orderQuoteSnapshotSchema.optional(),
  /**
   * Optional split between card vs SNAP/EBT funding (future runtime). Totals must
   * reconcile externally when both are set — not enforced here yet.
   */
  paymentSplit: z
    .object({
      cardPayCents: z.coerce.number().int().min(0).max(1_000_000).optional(),
      ebtSnapCents: z.coerce.number().int().min(0).max(1_000_000).optional()
    })
    .optional(),
  experimentIds: z.array(z.string().trim().min(3).max(100)).max(50).optional(),
  /**
   * Geocoded drop-off coordinates from checkout preview. Used for delivery
   * geofence enforcement (200m) at `DELIVERED` transition. Optional for
   * backward compatibility with older clients.
   */
  deliveryDropoffCoords: z
    .object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180)
    })
    .optional(),
  /** Required for unauthenticated guest checkout finalize. */
  guestEmail: z.string().trim().email().max(254).optional(),
  guestPhone: z.string().trim().max(32).optional(),
  paymentUiMode: z.enum(['embedded', 'hosted', 'payment_sheet']).optional(),
  /** Verified bottle return UPCs to include in this order (typically for guest checkout). */
  returnUpcs: z.array(z.object({
    upc: z.string().trim().min(8).max(32),
    quantity: z.coerce.number().int().min(1).max(500)
  })).max(250).optional(),
  /** When the shopper accepts Terms/Privacy at checkout, pin the public versions. */
  acceptedPolicies: z.boolean().optional(),
  termsVersion: z.string().trim().min(1).max(32).optional(),
  privacyVersion: z.string().trim().min(1).max(32).optional()
});

export const checkoutFinalizationPayloadSchema = checkoutFinalizationBaseSchema.refine(data => {
  if (data.isStandaloneReturn) return true;
  return (data.items && data.items.length > 0) || (data.customRequests && data.customRequests.length > 0);
}, "Order must contain at least one catalog item or custom request").refine(data => {
  return Boolean(data.quoteId || data.quoteSnapshot);
}, "Either quoteId or quoteSnapshot is required for checkout finalization");

type RefinementIssue = {
  code: typeof z.ZodIssueCode.custom;
  path: Array<string | number>;
  message: string;
  params?: Record<string, unknown>;
};

type CheckoutRefinementContext = {
  addIssue: (issue: RefinementIssue) => void;
};

export type QuoteAvailabilitySnapshotItem = ReturnType<typeof quoteAvailabilitySnapshotItemSchema.parse>;
export type ExtraRouteSnapshot = ReturnType<typeof extraRouteSnapshotSchema.parse>;
export type OrderQuoteSnapshot = ReturnType<typeof orderQuoteSnapshotSchema.parse>;
export type CheckoutFinalizationPayload = ReturnType<typeof checkoutFinalizationPayloadSchema.parse>;

export const requiresCheckoutAcknowledgmentRule = (
  value: OrderQuoteSnapshot,
  ctx: CheckoutRefinementContext
): void => {
  if (value.uncertainty.requiresCheckoutAcknowledgment && value.checkoutAcknowledgment.lowConfidenceAccepted !== true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['checkoutAcknowledgment', 'lowConfidenceAccepted'],
      message: 'lowConfidenceAccepted must be true when requiresCheckoutAcknowledgment is true',
      params: { code: 'LOW_CONFIDENCE_ACK_REQUIRED' }
    });
  }
};

export const requiresRetailerAuthorizationRule = (
  value: OrderQuoteSnapshot,
  ctx: CheckoutRefinementContext
): void => {
  const disclosure = value.fulfillmentDisclosure;
  if (!disclosure?.requiresRetailerAuthorization) return;
  if (value.checkoutAcknowledgment.retailerAuthorizationAccepted === true) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['checkoutAcknowledgment', 'retailerAuthorizationAccepted'],
    message: 'retailerAuthorizationAccepted must be true when retail fulfillment requires authorization',
    params: { code: 'RETAILER_AUTHORIZATION_REQUIRED' }
  });
};

export const requiresLineSubstituteAcknowledgmentRule = (
  value: OrderQuoteSnapshot,
  ctx: CheckoutRefinementContext
): void => {
  const disclosure = value.fulfillmentDisclosure;
  if (!disclosure?.requiresLineSubstituteAcknowledgment) return;
  if (value.checkoutAcknowledgment.lineSubstituteAccepted === true) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['checkoutAcknowledgment', 'lineSubstituteAccepted'],
    message: 'lineSubstituteAccepted must be true when substitute offers require acknowledgment',
    params: { code: 'LINE_SUBSTITUTE_ACK_REQUIRED' }
  });
};
